import estyled from '@signal/theme/emotion'
import { createSelector } from 'reselect'
import isString from 'lodash/isString'
import map from 'lodash/map'
import kebabCase from 'lodash/kebabCase'
import * as muiStylesLib from '@material-ui/core/styles'
import isOnSite from '@signal/utils/env/onSite'
import { colorSelectors } from '../../Typography'
import { palette } from '../styles'
import { keyframes } from '@emotion/css'

const LAST_ROW_WIDTH = 0.85 // 85%

function getPadding({ theme, variant }) {
  const { lineHeight, fontSize } = theme.typography[variant]
  // if we are on-site, then font-size has already been converted to px, which is why we can't just use
  // cleanupRemWhenOnSite here.  The scalar will be the same in both cases
  const scalar = (parseFloat(lineHeight) - parseFloat(fontSize)) / 2
  return `${scalar}${isOnSite() ? 'px' : 'rem'}`
}

function getWidth({ last, fill, width }) {
  let w = '100%'
  if (fill || width) w = fill ? '100%' : width
  let value = w
  let unit = 'px'
  if (isString(w)) {
    // If width is a string, separate the value from the unit
    const [strValue, strUnit] = w.match(/[a-zA-Z%]+|[0-9.]+/g)
    value = Number(strValue)
    unit = strUnit
  }
  // For last rows, multiply value by 85%
  return `${last ? value * LAST_ROW_WIDTH : value}${unit}`
}

function getMaxWidth({ last }) {
  const value = last ? LAST_ROW_WIDTH * 100 : 100
  return `${value}%`
}

function getHeight({ theme, variant, fill, height }) {
  let heightStr
  // If height is a string, it's good to go... if it's a number, we need to add
  // the px unit to the end so it's valid for CSS
  if (height) heightStr = isString(height) ? height : `${height}px`
  if (fill || heightStr) return fill ? '100%' : heightStr
  return theme.typography[variant].fontSize
}

function selectColorSelector({ inverse, color }) {
  if (color.startsWith('skeleton'))
    return inverse ? palette.inverse[color] : palette.default[color]
  // Non-"skeleton" colors are rendered with a slight alpha transparency to make
  // them look slightly more subdued. Without the transparency many of the
  // colors, for example "success" and "error" are way too bright and stick out
  // like a sore thumb when used with the skeleton.
  const libKeys = Object.keys(muiStylesLib)
  const libKey = libKeys.find(key => key === 'alpha') ? 'alpha' : 'fade'
  const fade = muiStylesLib[libKey]
  return createSelector(colorSelectors[color], color => fade(color, 0.65))
}

export const backgroundColorSelector = createSelector(
  selectColorSelector,
  props => props.theme,
  (skeletonColorSelector, theme) => skeletonColorSelector(theme)
)

const alignmentSelector = createSelector(
  props => props.align,
  align => {
    if (!align) return
    if (align === 'center') return 'margin: 0 auto;'
    return `float: ${align};`
  }
)

export const Wrapper = estyled.div`
  width: ${getWidth};
  ${alignmentSelector}
`

const shimmerAnimation = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
  `

export const Skeleton = estyled.div`
position: relative;
overflow: hidden;
  /* Use padding instead of margin so that vertical margins aren't collapsed. */
  padding: ${getPadding} 0;
  height: ${getHeight};
  max-width: ${getMaxWidth};
  box-sizing: content-box;
  /* Using a :before selector to avoid creating a new child DOM element. */
  &:before {
    content: ' ';
    background: ${backgroundColorSelector};
    height: ${getHeight};
    max-width: 100%;
    display: block;
    border-radius: ${({ theme, variant }) =>
      theme.typography[variant].fontSize};
    /* Loop through style object and convert to CSS. */
    ${props => map(props.styles, (val, key) => `${kebabCase(key)}: ${val};`)}
  }

    /* Add a shimmer animation to make it more obvious that this is a loading skeleton. */
  &:after {
    content: ' ';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: linear-gradient(
      to right,
      transparent 0%,
      rgba(255, 255, 255, 0.6) 50%,
      transparent 100%
    );
    animation: ${shimmerAnimation} 1.5s infinite;
  }
  /* Don't pass component props down to the div component, only className. */



`
